import React, { useState } from 'react';
import { Settings as SettingsIcon, Users, Bell, Shield, Database, Mail, ArrowLeft, Sliders } from 'lucide-react';
import SMTPConfiguration from '../components/settings/SMTPConfiguration';
import UserManagement from './UserManagement';
import WorkshopOptionsManagement from '../components/settings/WorkshopOptionsManagement';
import BackupSection from '../components/settings/BackupSection';
import { useIsAdmin } from '../stores/authStore';

const Settings: React.FC = () => {
  const [currentView, setCurrentView] = useState<'main' | 'smtp' | 'users' | 'workshop-options' | 'backups'>('main');
  const isAdmin = useIsAdmin();

  if (currentView === 'smtp') {
    return (
      <div className="p-6">
        {/* Back Button */}
        <button
          onClick={() => setCurrentView('main')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Settings
        </button>

        <SMTPConfiguration />
      </div>
    );
  }

  if (currentView === 'users') {
    return (
      <div className="p-6">
        {/* Back Button */}
        <button
          onClick={() => setCurrentView('main')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Settings
        </button>

        <UserManagement />
      </div>
    );
  }

  if (currentView === 'workshop-options') {
    return (
      <div className="p-6">
        {/* Back Button */}
        <button
          onClick={() => setCurrentView('main')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Settings
        </button>

        <WorkshopOptionsManagement />
      </div>
    );
  }

  if (currentView === 'backups') {
    return (
      <div className="p-6">
        {/* Back Button */}
        <button
          onClick={() => setCurrentView('main')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Settings
        </button>

        <BackupSection />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-gray-600">
          Configure system settings and preferences.
        </p>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Management */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
              <p className="text-sm text-gray-600">Manage users and permissions</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => setCurrentView('users')}
              className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="font-medium text-gray-900">Add New User</div>
              <div className="text-sm text-gray-600">Create user accounts and assign roles</div>
            </button>
            <button
              onClick={() => setCurrentView('users')}
              className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="font-medium text-gray-900">Role Management</div>
              <div className="text-sm text-gray-600">Configure user roles and permissions</div>
            </button>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <Bell className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
              <p className="text-sm text-gray-600">Configure notification settings</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => setCurrentView('smtp')}
              className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="font-medium text-gray-900">SMTP Configuration</div>
              <div className="text-sm text-gray-600">Configure email server settings</div>
            </button>
            <button className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="font-medium text-gray-900">Alert Preferences</div>
              <div className="text-sm text-gray-600">Set up system alerts and reminders</div>
            </button>
          </div>
        </div>

        {/* Security */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
              <Shield className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Security</h2>
              <p className="text-sm text-gray-600">Security and authentication settings</p>
            </div>
          </div>
          <div className="space-y-3">
            <button className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="font-medium text-gray-900">Password Policy</div>
              <div className="text-sm text-gray-600">Configure password requirements</div>
            </button>
            <button className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="font-medium text-gray-900">Session Management</div>
              <div className="text-sm text-gray-600">Manage user sessions and timeouts</div>
            </button>
          </div>
        </div>

        {/* Workshop Configuration - Admin Only */}
        {isAdmin && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                <Sliders className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Workshop Configuration</h2>
                <p className="text-sm text-gray-600">Manage dropdown options</p>
              </div>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => setCurrentView('workshop-options')}
                className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="font-medium text-gray-900">Dropdown Options</div>
                <div className="text-sm text-gray-600">Configure status, category, and other dropdown values</div>
              </button>
              <div className="px-4 py-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm text-blue-800">
                  <strong>427 options</strong> across 14 attributes
                </div>
              </div>
            </div>
          </div>
        )}

        {/* System */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <Database className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">System</h2>
              <p className="text-sm text-gray-600">System configuration and maintenance</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => setCurrentView('backups')}
              className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="font-medium text-gray-900">System Backups</div>
              <div className="text-sm text-gray-600">Download database, media files, and logs</div>
            </button>
          </div>
        </div>
      </div>

      {/* Coming Soon Notice */}
      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <SettingsIcon className="h-5 w-5 text-yellow-600 mr-2" />
          <div>
            <h3 className="font-medium text-yellow-800">Settings Panel Under Development</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Advanced configuration options are being developed. Basic settings are available through the user menu.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
